import { useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Compact mic button for dictating straight into a single field.
export default function FieldDictate({ onText, title = "Dictate this field", size = "sm", testid = "field-dictate-btn" }) {
  const lg = size === "lg";
  const [state, setState] = useState("idle");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async (e) => {
    e.preventDefault();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (ev) => ev.data.size > 0 && chunksRef.current.push(ev.data);
      mr.onstop = handleStop;
      mr.start();
      mediaRef.current = mr;
      setState("recording");
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const stop = (e) => {
    e?.preventDefault();
    if (mediaRef.current && state === "recording") {
      mediaRef.current.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    }
  };

  const handleStop = async () => {
    setState("transcribing");
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size === 0) return setState("idle");
    const fd = new FormData();
    fd.append("audio", blob, "field.webm");
    try {
      const { data } = await api.post("/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.text?.trim()) onText(data.text.trim());
    } catch {
      toast.error("Couldn't transcribe.");
    } finally {
      setState("idle");
    }
  };

  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onClick={state === "recording" ? stop : start}
      disabled={state === "transcribing"}
      className={`inline-flex items-center justify-center ${lg ? "h-12 px-5 gap-2 rounded-full text-sm font-semibold shadow-sm" : "w-6 h-6 rounded-md"} border transition-colors ${
        state === "recording"
          ? "bg-[#C26D5C] border-[#a9553f] text-white"
          : "bg-white border-[#E2DFD8] text-[#7A8C82] hover:text-[#4A6B53] hover:border-[#4A6B53]"
      }`}
    >
      {state === "transcribing" ? (
        <Loader2 className={lg ? "w-4 h-4 animate-spin" : "w-3 h-3 animate-spin"} />
      ) : state === "recording" ? (
        <Square className={lg ? "w-3.5 h-3.5 fill-white" : "w-2.5 h-2.5 fill-white"} />
      ) : (
        <Mic className={lg ? "w-4 h-4" : "w-3 h-3"} strokeWidth={2} />
      )}
      {lg && (state === "transcribing" ? "Writing…" : state === "recording" ? "Stop" : "Dictate")}
    </button>
  );
}
